// WrittenPrepPage — Select a topic, write notes with Anna's help
// Phase 3: New component for the Written Preparation area

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { getTopicsByJourney } from '../../services/firebase';
import { getTopicNote, saveTopicNote } from '../../services/firebase/topicNoteService';
import Layout from '../layout/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import PrepAnnaChat from './PrepAnnaChat';
import ExamNotesPanel from './ExamNotesPanel';
import { FindingsInbox } from './FindingsInbox';
import ConceptMap from '../visualizations/ConceptMap';
import ConceptMapHeader from '../visualizations/ConceptMapHeader';
import { CrossTopicView } from './CrossTopicView';
import { exportExamNotes } from '../../utils/exportExamNotes';
import { generateNoteSkeleton } from '../../utils/generateNoteSkeleton';
import type { Topic, TopicNote, ExamConcept, BloomLevel } from '../../types';
import type { Finding } from '../../types/findings';

// Bloom level ordering for deriving highest level from concept map nodes
const BLOOM_ORDER: BloomLevel[] = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];

function highestBloomLevel(levels: BloomLevel[]): BloomLevel {
  if (levels.length === 0) return 'recognize';
  return levels.reduce((best, cur) =>
    BLOOM_ORDER.indexOf(cur) > BLOOM_ORDER.indexOf(best) ? cur : best
  , levels[0]);
}

const WrittenPrepPage: React.FC = () => {
  const { t, i18n } = useTranslation('preparation');
  const navigate = useNavigate();
  const { topicId, journeyId } = useParams<{ topicId?: string; journeyId?: string }>();
  const { user } = useAuth();
  const { journeys } = useLearningJourneys();

  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<TopicNote | null>(null);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [activeView, setActiveView] = useState<'notes' | 'exam'>('notes');
  const [qualifyingConcept, setQualifyingConcept] = useState<ExamConcept | null>(null);
  const [rightTab, setRightTab] = useState<'conceptMap' | 'anna' | 'exam'>('conceptMap');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedJourney = journeyId ? journeys.find(j => j.id === journeyId) : null;
  const selectedTopic = topicId ? topics.find(tp => tp.id === topicId) : null;

  // Derived exam data from note
  const examConcepts: ExamConcept[] = note?.examConcepts || [];
  const sessionKeywords: string[] = note?.sessionKeywords || [];
  const hasNoExamData = examConcepts.length === 0;

  // Derived concept map and findings data
  const findings: Finding[] = note?.findings ?? [];
  const conceptMapData = note?.conceptMap ?? null;

  // Load topics when journey is selected
  useEffect(() => {
    if (!journeyId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const result = await getTopicsByJourney(journeyId);
      if (!cancelled && result.success && result.topics) {
        setTopics(result.topics);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [journeyId]);

  // Load existing note when topic is selected
  useEffect(() => {
    if (!topicId || !user?.uid) return;
    let cancelled = false;
    const load = async () => {
      const result = await getTopicNote(user.uid, topicId);
      if (!cancelled && result.success) {
        setNote(result.data || null);
        setContent(result.data?.content || '');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [topicId, user?.uid]);

  // Auto-generate skeleton when note is empty but concept map exists
  useEffect(() => {
    if (
      note &&
      conceptMapData &&
      conceptMapData.nodes.length > 0 &&
      !note.skeletonGenerated &&
      (!note.content || note.content.trim() === '')
    ) {
      const skeleton = generateNoteSkeleton(conceptMapData, i18n.language === 'da' ? 'da' : 'en');
      setContent(skeleton);
      const noteRef = doc(db, 'topicNotes', note.id);
      setDoc(noteRef, { content: skeleton, skeletonGenerated: true }, { merge: true });
    }
  }, [note, conceptMapData, i18n.language]);

  // Auto-save after 2 seconds of inactivity
  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave(newContent);
    }, 2000);
  }, [topicId, journeyId, user?.uid, selectedTopic]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = useCallback(async (contentToSave?: string) => {
    if (!user?.uid || !topicId || !journeyId || !selectedTopic) return;
    const text = contentToSave ?? content;
    if (!text.trim() && !note) return; // Don't save empty initial note

    setSaving(true);
    const result = await saveTopicNote({
      userId: user.uid,
      topicId,
      journeyId,
      topicTitle: selectedTopic.title,
      content: text,
    });
    if (result.success) {
      setLastSaved(new Date());
    }
    setSaving(false);
  }, [user?.uid, topicId, journeyId, selectedTopic, content, note]);

  // Handle note update from Anna's suggestion
  const handleNoteUpdate = useCallback((newContent: string, _summary: string) => {
    setContent(newContent);
    // Trigger auto-save
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave(newContent);
    }, 1000);
  }, [handleSave]);

  // ---- Findings handlers ----

  function getBloomSectionHeader(bloomLevel: string | undefined): string {
    const map: Record<string, string> = {
      create: t('bloomHeaders.application'),
      evaluate: t('bloomHeaders.application'),
      analyze: t('bloomHeaders.application'),
      apply: t('bloomHeaders.application'),
      understand: t('bloomHeaders.understanding'),
      recognize: t('bloomHeaders.knowledge'),
    };
    return (bloomLevel && map[bloomLevel]) ?? t('bloomHeaders.notStarted');
  }

  function findSectionEnd(text: string, sectionStart: number): number {
    const nextH2 = text.indexOf('\n## ', sectionStart + 1);
    return nextH2 === -1 ? text.length : nextH2;
  }

  const handleIntegrateFinding = useCallback(async (finding: Finding) => {
    if (!note) return;
    const updatedFindings = (note.findings ?? []).map(f =>
      f.id === finding.id ? { ...f, status: 'integrated' as const } : f
    );

    let updatedContent = content;
    const lang = i18n.language;

    if (finding.type === 'node_added' && finding.data.label) {
      const bloomSection = getBloomSectionHeader(finding.data.bloomLevel);
      const sectionIndex = updatedContent.indexOf(bloomSection);
      if (sectionIndex !== -1) {
        const insertPoint = findSectionEnd(updatedContent, sectionIndex);
        const placeholder = t('findings.notesPlaceholder');
        const newBlock = `\n### ${finding.data.label}\n\n${placeholder}\n`;
        updatedContent = updatedContent.slice(0, insertPoint) + newBlock + updatedContent.slice(insertPoint);
      }
    } else if (finding.type === 'edge_added' && finding.data.edgeSource && finding.data.edgeTarget) {
      const sourceLabel = conceptMapData?.nodes.find(n => n.id === finding.data.edgeSource)?.label;
      const targetLabel = conceptMapData?.nodes.find(n => n.id === finding.data.edgeTarget)?.label;
      if (sourceLabel && targetLabel) {
        const symbol = finding.data.relationType === 'causal' ? '→'
          : finding.data.relationType === 'interdependent' ? '↔' : '—';
        const prep = lang === 'da' ? 'til' : 'to';
        const relLine = `> ${symbol} ${finding.data.relationType} ${prep} ${targetLabel}\n`;
        const headingIndex = updatedContent.indexOf(`### ${sourceLabel}`);
        if (headingIndex !== -1) {
          const lineEnd = updatedContent.indexOf('\n', headingIndex);
          updatedContent = updatedContent.slice(0, lineEnd + 1) + relLine + updatedContent.slice(lineEnd + 1);
        }
      }
    }
    // bloom_updated: just update status for v1 (section moving is complex markdown manipulation)

    setContent(updatedContent);
    // Optimistic update before write so UI feels instant
    setNote(prev => prev ? { ...prev, content: updatedContent, findings: updatedFindings } : prev);
    const noteRef = doc(db, 'topicNotes', note.id);
    try {
      await setDoc(noteRef, { content: updatedContent, findings: updatedFindings }, { merge: true });
    } catch (error) {
      console.error('WrittenPrepPage: failed to persist integrated finding', error);
    }
  }, [note, content, conceptMapData, i18n.language]);

  const handleDismissFinding = useCallback(async (finding: Finding) => {
    if (!note) return;
    const updatedFindings = (note.findings ?? []).map(f =>
      f.id === finding.id ? { ...f, status: 'dismissed' as const } : f
    );
    // Optimistic update before write so UI feels instant
    setNote(prev => prev ? { ...prev, findings: updatedFindings } : prev);
    const noteRef = doc(db, 'topicNotes', note.id);
    try {
      await setDoc(noteRef, { findings: updatedFindings }, { merge: true });
    } catch (error) {
      console.error('WrittenPrepPage: failed to persist dismissed finding', error);
    }
  }, [note]);

  // ---- Exam concept handlers ----
  const saveNoteWithConcepts = useCallback(async (updatedConcepts: ExamConcept[]) => {
    if (!user?.uid || !topicId || !journeyId || !selectedTopic) return;

    // Optimistic update — show concept immediately
    setNote(prev => prev
      ? { ...prev, examConcepts: updatedConcepts }
      : {
          id: `${user.uid}_${topicId}`,
          userId: user.uid,
          topicId,
          journeyId,
          topicTitle: selectedTopic.title,
          content: content,
          sessionKeywords: [],
          examConcepts: updatedConcepts,
          createdAt: new Date(),
          updatedAt: new Date(),
        }
    );

    // Persist to Firestore in background
    setSaving(true);
    const result = await saveTopicNote({
      userId: user.uid,
      topicId,
      journeyId,
      topicTitle: selectedTopic.title,
      content: note?.content || content,
      sessionKeywords: note?.sessionKeywords || [],
      examConcepts: updatedConcepts,
    });
    if (result.success) {
      setLastSaved(new Date());
    }
    setSaving(false);
  }, [user?.uid, topicId, journeyId, selectedTopic, note, content]);

  const handleConceptAdd = useCallback((concept: Omit<ExamConcept, 'id' | 'createdAt'>) => {
    const newConcept: ExamConcept = {
      ...concept,
      id: `concept-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      createdAt: new Date(),
    };
    saveNoteWithConcepts([...examConcepts, newConcept]);
  }, [examConcepts, saveNoteWithConcepts]);

  const handleConceptUpdate = useCallback((id: string, updates: Partial<ExamConcept>) => {
    const updated = examConcepts.map(c => c.id === id ? { ...c, ...updates } : c);
    saveNoteWithConcepts(updated);
  }, [examConcepts, saveNoteWithConcepts]);

  const handleConceptDelete = useCallback((id: string) => {
    const filtered = examConcepts.filter(c => c.id !== id);
    saveNoteWithConcepts(filtered);
  }, [examConcepts, saveNoteWithConcepts]);

  const handleStartQualification = useCallback((concept: ExamConcept) => {
    setQualifyingConcept(concept);
    setShowChat(true);
  }, []);

  const handleConceptQualified = useCallback((conceptId: string) => {
    handleConceptUpdate(conceptId, { qualified: true });
    setQualifyingConcept(null);
  }, [handleConceptUpdate]);

  const handleExport = useCallback(() => {
    if (selectedTopic && examConcepts.length > 0) {
      exportExamNotes(selectedTopic.title, examConcepts);
    }
  }, [selectedTopic, examConcepts]);

  // Cleanup auto-save timer
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  // ---- Journey selection view ----
  if (!journeyId) {
    const activeJourneys = journeys.filter(j => j.status === 'active');
    return (
      <Layout>
        <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-neutral-dark dark:text-white mb-6">
            {t('title')}
          </h1>
          <p className="text-neutral-medium mb-6">{t('selectJourney')}</p>

          {activeJourneys.length === 0 ? (
            <EmptyState
              type="journeys"
              title={t('noJourneys')}
              description={t('noJourneysDescription')}
              actionText={t('createJourney')}
              onAction={() => navigate('/journeys/new')}
            />
          ) : (
            <div className="space-y-3">
              {activeJourneys.map(journey => (
                <Card
                  key={journey.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/preparation/${journey.id}`)}
                >
                  <h3 className="font-semibold text-neutral-dark dark:text-white">
                    {journey.title || journey.subject}
                  </h3>
                </Card>
              ))}
            </div>
          )}
        </div>
        </div>
      </Layout>
    );
  }

  // ---- Topic selection view ----
  if (!topicId) {
    return (
      <Layout>
        <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate('/preparation')}
            className="text-sm text-primary-600 hover:text-primary-700 mb-2 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('backToJourneys')}
          </button>

          <h1 className="text-2xl font-bold text-neutral-dark dark:text-white mb-2">
            {selectedJourney?.title || selectedJourney?.subject}
          </h1>
          <p className="text-neutral-medium mb-6">{t('selectTopic')}</p>

          {loading ? (
            <div className="text-center py-12 text-neutral-medium">{t('common:loading')}</div>
          ) : topics.length === 0 ? (
            <EmptyState
              type="topics"
              title={t('noTopics')}
              description={t('noTopicsDescription')}
            />
          ) : (
            <div className="space-y-3">
              {topics.map(topic => (
                <Card
                  key={topic.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/preparation/${journeyId}/${topic.id}`)}
                >
                  <h3 className="font-medium text-neutral-dark dark:text-white">
                    {topic.title}
                  </h3>
                  <p className="text-sm text-neutral-medium mt-1">
                    {(topic.subtopics || []).map(s => s.title).join(' · ')}
                  </p>
                </Card>
              ))}
              {user?.uid && <CrossTopicView userId={user.uid} journeyId={journeyId} />}
            </div>
          )}
        </div>
        </div>
      </Layout>
    );
  }

  // ---- Note editor view with chat ----
  return (
    <Layout>
      <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <button
          onClick={() => navigate(`/preparation/${journeyId}`)}
          className="text-sm text-primary-600 hover:text-primary-700 mb-2 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('backToTopics')}
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark dark:text-white">
              {selectedTopic?.title || t('title')}
            </h1>
            {selectedTopic?.subtopics && selectedTopic.subtopics.length > 0 && (
              <p className="text-sm text-neutral-medium mt-1">
                {selectedTopic.subtopics.map(s => s.title).join(' \u00b7 ')}
              </p>
            )}
          </div>
          {journeyId && topicId && (
            <button
              onClick={() => navigate(`/workspace/${journeyId}/${topicId}`)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
            >
              📊 {t('openWorkspace')}
            </button>
          )}
          <div className="flex items-center gap-3">
            {saving && (
              <span className="text-sm text-neutral-medium">{t('saving')}</span>
            )}
            {!saving && lastSaved && (
              <span className="text-sm text-neutral-medium">{t('saved')}</span>
            )}
            {activeView === 'notes' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSave()}
                disabled={saving}
              >
                {t('save')}
              </Button>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveView('notes')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'notes'
                ? 'bg-white dark:bg-gray-700 text-neutral-dark dark:text-white shadow-sm'
                : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-300'
            }`}
          >
            {t('tabs.notes', 'Noter')}
          </button>
          <button
            onClick={() => setActiveView('exam')}
            className={`relative px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'exam'
                ? 'bg-white dark:bg-gray-700 text-neutral-dark dark:text-white shadow-sm'
                : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-300'
            }`}
          >
            {t('tabs.exam', 'Eksamensforberedelse')}
            {/* "Ny" badge when no concepts exist yet */}
            {hasNoExamData && activeView !== 'exam' && (
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-accent-orange text-white rounded-full leading-none">
                {t('tabs.newBadge', 'Ny')}
              </span>
            )}
          </button>
        </div>

        {/* Desktop: side-by-side 60/40 | Mobile: toggle */}
        <div className="flex gap-4">
          {/* Left panel — hidden on mobile when chat is open */}
          <div className={`${showChat ? 'hidden md:block' : 'block'} w-full md:w-3/5`}>
            {activeView === 'notes' ? (
              <>
                {/* Findings inbox — shows pending concept map changes */}
                <FindingsInbox
                  findings={findings}
                  onIntegrate={handleIntegrateFinding}
                  onDismiss={handleDismissFinding}
                />

                <Card className="min-h-[60vh]">
                  <textarea
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder={t('placeholder')}
                    className="w-full h-full min-h-[55vh] p-4 bg-transparent text-neutral-dark dark:text-white placeholder-neutral-medium resize-none focus:outline-none font-mono text-sm leading-relaxed"
                  />
                </Card>

                {/* Mobile: "Ask Anna" button */}
                <div className="mt-3 md:hidden">
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => setShowChat(true)}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    {t('chat.askAnna')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <ExamNotesPanel
                  examConcepts={examConcepts}
                  sessionKeywords={sessionKeywords}
                  topicTitle={selectedTopic?.title || ''}
                  onConceptAdd={handleConceptAdd}
                  onConceptUpdate={handleConceptUpdate}
                  onConceptDelete={handleConceptDelete}
                  onStartQualification={handleStartQualification}
                  onExport={handleExport}
                  conceptMapNodes={conceptMapData?.nodes}
                />

                {/* Mobile: "Ask Anna" button */}
                <div className="mt-3 md:hidden">
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => setShowChat(true)}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    {t('chat.askAnna')}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Right panel — tabbed: Begrebskort / Anna / Eksamen */}
          <div className={`${showChat ? 'block' : 'hidden md:block'} w-full md:w-2/5 flex flex-col border-l border-gray-200 dark:border-gray-800`}>
            {/* Mobile: back button */}
            <div className="mb-2 md:hidden">
              <button
                onClick={() => { setShowChat(false); setQualifyingConcept(null); }}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('chat.backToNotes')}
              </button>
            </div>

            {/* Right panel tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              {(['conceptMap', 'anna', 'exam'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setRightTab(tab)}
                  className={`px-4 py-2 text-xs font-medium transition-colors ${
                    rightTab === tab
                      ? 'text-yellow-600 dark:text-yellow-400 border-b-2 border-yellow-500 dark:border-yellow-400'
                      : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-gray-400'
                  }`}
                >
                  {t(`rightPanel.${tab}`)}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto">
              {rightTab === 'conceptMap' && conceptMapData && (
                <div className="flex flex-col h-full">
                  {conceptMapData.meta && (
                    <ConceptMapHeader
                      currentBloomLevel={highestBloomLevel(
                        conceptMapData.nodes.map(n => n.bloomLevel)
                      )}
                      totalSessions={conceptMapData.meta.totalSessions}
                    />
                  )}
                  <div className="flex-1" style={{ minHeight: '300px' }}>
                    <ConceptMap
                      nodes={conceptMapData.nodes.map(n => ({
                        id: n.id,
                        type: 'conceptMapNode',
                        position: n.position ?? { x: 0, y: 0 },
                        data: { label: n.label, bloomLevel: n.bloomLevel, pinned: n.pinned },
                      }))}
                      edges={conceptMapData.edges.map(e => ({
                        id: e.id,
                        source: e.source,
                        target: e.target,
                        label: e.label,
                        data: { relationType: e.relationType },
                      }))}
                      readOnly={true}
                      isLoading={false}
                      onNodeDragStop={() => {}}
                    />
                  </div>
                </div>
              )}
              {rightTab === 'conceptMap' && !conceptMapData && (
                <div className="flex items-center justify-center h-full text-neutral-medium text-sm p-4">
                  {t('rightPanel.noConceptMap')}
                </div>
              )}
              {rightTab === 'anna' && (
                <div className="h-[60vh] md:h-[65vh]">
                  {selectedTopic && (
                    <PrepAnnaChat
                      topicTitle={selectedTopic.title}
                      subtopics={selectedTopic.subtopics || []}
                      currentNotes={content}
                      onNoteUpdate={handleNoteUpdate}
                      examConcept={qualifyingConcept}
                      onConceptQualified={handleConceptQualified}
                    />
                  )}
                </div>
              )}
              {rightTab === 'exam' && (
                <ExamNotesPanel
                  examConcepts={examConcepts}
                  sessionKeywords={sessionKeywords}
                  topicTitle={selectedTopic?.title || ''}
                  onConceptAdd={handleConceptAdd}
                  onConceptUpdate={handleConceptUpdate}
                  onConceptDelete={handleConceptDelete}
                  onStartQualification={handleStartQualification}
                  onExport={handleExport}
                  conceptMapNodes={conceptMapData?.nodes}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default WrittenPrepPage;
